import { useBemCN } from "@gik/core/utils/bemBlock";
import withComponentErrorBoundary from "@gik/core/utils/withComponentErrorBoundary";
import { CreateClickedLocation } from "@gik/create/enums/CreateAnalyticsEnums";
import { LandingPageStructuredData } from "@gik/seo/components/StructuredData";
import { Layout } from "@gik/ui-legacy/gik/Layout/Layout";
import React from "react";
import { BlogSection } from "../sections/BlogSection";
import type { ICTASectionProps } from "../sections/CTASection";
import { CTASection } from "../sections/CTASection";
import { FAQSection } from "../sections/FAQSection";
import type { IFeatureFocusSectionProps } from "../sections/FeatureFocusSection";
import { FeatureFocusSection } from "../sections/FeatureFocusSection";
import { FeaturesSection } from "../sections/FeaturesSection";
import type { IHeroSectionProps } from "../sections/LandingPageHeroSection";
import { LandingPageHeroSection } from "../sections/LandingPageHeroSection";
import { TestimonialsSection } from "../sections/TestimonialsSection";

export const LandingPage = withComponentErrorBoundary(LandingPageComp);

export interface ILandingPageProps {
	heroSection: IHeroSectionProps;
	featureFocus: IFeatureFocusSectionProps;
	ctaSection: ICTASectionProps;
	blogSection: IBlogSectionProps;
	url?: string;
	className?: string;
	ctaLabel?: string;
	ctaHref?: string;
	onCtaClick?: () => void;
}

/**
 * https://www.figma.com/design/FT2ninjhNiQOXfnOTaZfqy/%F0%9F%91%A8%F0%9F%8F%BC%E2%80%8D%F0%9F%8E%A4-Jade's-workspace-of-asset-creation?node-id=4696-8640&m=dev
 */
function LandingPageComp({
	heroSection,
	featureFocus,
	ctaSection,
	blogSection,
	url,
	className,
	ctaLabel,
	ctaHref,
	onCtaClick,
}: ILandingPageProps) {
	const bem = useBemCN("landing-section");

	const content = (
		<Layout
			element={"div"}
			direction={"column"}
			{...bem(null, null, className)}
		>
			<LandingPageHeroSection {...heroSection} ctaLabel={ctaLabel} ctaHref={ctaHref} onCtaClick={onCtaClick} />
			<FeatureFocusSection {...featureFocus} />
			<CTASection {...ctaSection} />
			<FeaturesSection
				trackCreateClickedLocation={CreateClickedLocation.LANDINGPAGE_HALF}
				ctaLabel={ctaLabel}
				ctaHref={ctaHref}
				onCtaClick={onCtaClick}
			/>
			<TestimonialsSection />
			{blogSection && <BlogSection {...blogSection} />}
			<FAQSection />
		</Layout>
	);

	if (url) {
		return (
			<LandingPageStructuredData
				page={{
					name: heroSection.hero.title,
					description: heroSection.hero.content,
					url: `https://www.giveinkind.com${url}`,
				}}
			>
				{content}
			</LandingPageStructuredData>
		);
	}

	return content;
}
